import { formatMinuteOfDay, WEEKDAYS_PT } from "../dates";

/** Faixa dentro de um dia da semana, em minutos desde 00h00 (0 = domingo). */
export interface DayRange {
  weekday: number;
  start: number;
  end: number;
}

export function rangeMinutes(r: DayRange): number {
  return Math.max(0, r.end - r.start);
}

export function totalMinutes(ranges: DayRange[]): number {
  return ranges.reduce((acc, r) => acc + rangeMinutes(r), 0);
}

/** Ordena e funde faixas sobrepostas ou encostadas do mesmo dia. */
export function mergeRanges(ranges: DayRange[]): DayRange[] {
  const sorted = ranges
    .filter((r) => r.end > r.start)
    .map((r) => ({ ...r }))
    .sort((a, b) => a.weekday - b.weekday || a.start - b.start);
  const out: DayRange[] = [];
  for (const r of sorted) {
    const last = out[out.length - 1];
    if (last && last.weekday === r.weekday && r.start <= last.end) {
      last.end = Math.max(last.end, r.end);
    } else {
      out.push(r);
    }
  }
  return out;
}

export function subtractRanges(base: DayRange[], remove: DayRange[]): DayRange[] {
  const out: DayRange[] = [];
  for (const b of base) {
    let pieces: { start: number; end: number }[] = [{ start: b.start, end: b.end }];
    for (const r of remove) {
      if (r.weekday !== b.weekday) continue;
      const next: { start: number; end: number }[] = [];
      for (const p of pieces) {
        if (r.end <= p.start || r.start >= p.end) {
          next.push(p);
          continue;
        }
        if (r.start > p.start) next.push({ start: p.start, end: r.start });
        if (r.end < p.end) next.push({ start: r.end, end: p.end });
      }
      pieces = next;
    }
    for (const p of pieces) if (p.end > p.start) out.push({ weekday: b.weekday, ...p });
  }
  return mergeRanges(out);
}

export function intersectRanges(a: DayRange[], b: DayRange[]): DayRange[] {
  const out: DayRange[] = [];
  for (const x of a) {
    for (const y of b) {
      if (x.weekday !== y.weekday) continue;
      const start = Math.max(x.start, y.start);
      const end = Math.min(x.end, y.end);
      if (end > start) out.push({ weekday: x.weekday, start, end });
    }
  }
  return mergeRanges(out);
}

export function rangeContains(outer: DayRange, inner: DayRange): boolean {
  return outer.weekday === inner.weekday && inner.start >= outer.start && inner.end <= outer.end;
}

export function rangesOverlap(a: DayRange, b: DayRange): boolean {
  return a.weekday === b.weekday && a.start < b.end && b.start < a.end;
}

/** "segunda, 19h00 às 20h00" */
export function formatRange(r: DayRange): string {
  return `${WEEKDAYS_PT[r.weekday]}, ${formatMinuteOfDay(r.start)} às ${formatMinuteOfDay(r.end)}`;
}

/** "segunda e quarta, 19h00 às 20h00" quando o horário é o mesmo; senão lista cada um. */
export function formatSlot(ranges: DayRange[]): string {
  const sorted = [...ranges].sort((a, b) => a.weekday - b.weekday || a.start - b.start);
  if (sorted.length === 0) return "";
  const first = sorted[0] as DayRange;
  const sameTime = sorted.every((r) => r.start === first.start && r.end === first.end);
  if (sameTime && sorted.length > 1) {
    const days = sorted.map((r) => WEEKDAYS_PT[r.weekday]);
    const label = `${days.slice(0, -1).join(", ")} e ${days[days.length - 1]}`;
    return `${label}, ${formatMinuteOfDay(first.start)} às ${formatMinuteOfDay(first.end)}`;
  }
  return sorted.map(formatRange).join("; ");
}
